import { useState } from "react";
import PageHeader from "@/components/common/PageHeader";
import SeoHead from "@/components/common/SeoHead";
import { FOUNDATION } from "@/data/content";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";
import { Mail, Phone, MapPin, Clock, CheckCircle2, Send, Instagram, Facebook, Linkedin, Youtube, Twitter, MessageCircle } from "lucide-react";
import { getImageUrl } from "@/lib/api";

const SOCIAL_ICONS = { instagram: Instagram, facebook: Facebook, linkedin: Linkedin, youtube: Youtube, twitter: Twitter, whatsapp: MessageCircle, tiktok: MessageCircle };

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const { settings } = useSettings();
  const c = settings?.contact || {};
  const pageData = settings?.contact_page || {};
  const email = c.email || FOUNDATION.contact.email;
  const phones = c.phones || FOUNDATION.contact.phones;
  const address1 = c.address_line1 || FOUNDATION.contact.address;
  const address2 = c.address_line2 || FOUNDATION.contact.addressLine2;
  const address3 = c.address_line3 || FOUNDATION.contact.addressLine3;
  const hours = c.office_hours || FOUNDATION.contact.officeHours;
  const mapSrc = c.map_embed_src || "https://www.google.com/maps?q=Padalarang%20Bandung%20Barat&output=embed";
  const socials = c.socials || {};

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSuccess(true);
      toast.success("Pesan Anda telah kami terima. Kami akan segera merespons.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <SeoHead
        title={{ id: "Kontak", en: "Contact" }}
        description={{ id: pageData.description || "Hubungi Sauyunan Foundation untuk kolaborasi, kemitraan, dan pertanyaan program.", en: "Contact Sauyunan Foundation for collaboration, partnership, and program inquiries." }}
      />
      <PageHeader
        eyebrow={pageData.eyebrow || "Kontak"}
        title={pageData.title || "Mari terhubung dan bekerja bersama"}
        description={pageData.description || "Kami terbuka untuk kolaborasi, kemitraan, pertanyaan program, dan kunjungan ke kantor kami di Padalarang."}
        image={pageData.image ? getImageUrl(pageData.image) : undefined}
      />

      <section className="section-y bg-white">
        <div className="container-sf grid lg:grid-cols-[1fr,1.3fr] gap-10">
          {/* Contact info */}
          <div>
            <div className="eyebrow mb-3">Info Kontak</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 leading-tight mb-8">Hubungi kami</h2>

            <div className="space-y-5">
              <InfoBlock icon={MapPin} label="Alamat">
                {address1}<br />
                {address2}<br />
                {address3}
              </InfoBlock>
              <InfoBlock icon={Mail} label="Email">
                <a href={`mailto:${email}`} className="text-[var(--brand-blue)] hover:text-[var(--brand-orange)]">{email}</a>
              </InfoBlock>
              <InfoBlock icon={Phone} label="Telepon">
                <div className="flex flex-col">
                  {phones.map((p) => <a key={p} href={`tel:${p.replace(/\s/g,'')}`} className="hover:text-[var(--brand-blue)]">{p}</a>)}
                </div>
              </InfoBlock>
              <InfoBlock icon={Clock} label="Jam Operasional">
                {hours}
              </InfoBlock>
            </div>

            <div className="mt-8">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Sosial Media</div>
              <div className="flex items-center gap-3 flex-wrap">
                {Object.entries(socials).filter(([, url]) => url).map(([key, url]) => {
                  const Icon = SOCIAL_ICONS[key] || MessageCircle;
                  return (
                    <a key={key} href={url} target="_blank" rel="noreferrer" aria-label={key} className="h-10 w-10 rounded-full bg-slate-100 hover:bg-[var(--brand-orange)] hover:text-white text-slate-700 flex items-center justify-center transition">
                      <Icon className="h-4 w-4" />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Form */}
          <div>
            {success ? (
              <div className="card-sf p-10 text-center" data-testid="contact-success">
                <CheckCircle2 className="h-14 w-14 text-[var(--brand-orange)] mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Pesan terkirim!</h3>
                <p className="text-slate-600 mb-6">Kami telah menerima pesan Anda dan akan merespons dalam 1–3 hari kerja.</p>
                <button onClick={() => setSuccess(false)} className="btn-outline">Kirim pesan lain</button>
              </div>
            ) : (
              <form onSubmit={submit} className="card-sf p-8" data-testid="contact-form">
                <h3 className="text-2xl font-bold text-slate-900 mb-6">Kirim Pesan</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input label="Nama Lengkap" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="contact-name" />
                  <Input label="Email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="contact-email" />
                  <Input label="Telepon" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="contact-phone" />
                  <Input label="Subjek" required value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testId="contact-subject" />
                </div>
                <div className="mt-4">
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Pesan Anda <span className="text-[var(--brand-orange)]">*</span></label>
                  <textarea
                    rows={6}
                    required
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="contact-message"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[var(--brand-blue)]"
                    placeholder="Tuliskan pesan Anda..."
                  />
                </div>
                <button type="submit" disabled={submitting} data-testid="contact-submit" className="btn-primary mt-6 w-full justify-center">
                  {submitting ? "Mengirim..." : "Kirim Pesan"} <Send className="h-4 w-4" />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="pb-24">
        <div className="container-sf">
          <div className="rounded-3xl overflow-hidden bg-slate-100 h-[420px] shadow-lg" data-testid="contact-map">
            <iframe
              title="Lokasi Sauyunan Foundation"
              src={getImageUrl(mapSrc)}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoBlock({ icon: Icon, label, children }) {
  return (
    <div className="flex gap-4">
      <div className="h-11 w-11 rounded-xl bg-[var(--brand-soft-blue)] text-[var(--brand-blue)] flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</div>
        <div className="text-sm md:text-base text-slate-700 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function Input({ label, type = "text", value, onChange, required, testId }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-700 mb-1.5">{label}{required && <span className="text-[var(--brand-orange)]"> *</span>}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testId}
        className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[var(--brand-blue)]"
      />
    </div>
  );
}
